import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a daily-dividend stock?',
    a: 'A daily-dividend stock pays a small cash dividend every US business day instead of a lump-sum monthly, quarterly, or annual payment. Historically no publicly-traded stock did this — dividend administration was too expensive. Strive, Inc. broke that ceiling on June 16, 2026 when it began paying daily cash distributions on its 13.00% SATA preferred stock, becoming the first stock in modern US market history to pay every business day.',
  },
  {
    q: 'How does SATA\u2019s daily dividend actually work?',
    a: 'Every US business day (roughly 252 per year, weekends and NYSE holidays excluded) is its own ex-dividend, record, and pay date cycle. If you own SATA at market close on Monday, you\u2019re the holder of record and receive Monday\u2019s payout (currently $0.0516 per share) on Tuesday. The annual rate stays fixed at 13.00% of the $100 stated value ($13.00 per share), and the per-day amount adjusts each month so it always divides evenly across that month\u2019s trading days.',
  },
  {
    q: 'Do Strategy\u2019s STRC, STRF, STRK and STRD work the same way as SATA?',
    a: 'Almost — with one twist. On October 28, 2026 Strategy Inc. shareholders approved (99.3% in favor) moving all four preferreds to daily dividends: STRC starts with its first daily record date on November 1, 2026 (first payout November 2), and STRF, STRK and STRD follow on January 1, 2027 (payouts from January 4). Unlike SATA, the dividend accrues every CALENDAR day, weekends and holidays included \u2014 365 accrual days a year, not 252. Because the shares clear through DTC, Saturday and Sunday\u2019s accrual is bundled onto Friday\u2019s record date and paid the next business day, so a Friday payment is roughly three times a Tuesday payment. The annual rate does not change (STRF 10%, STRK 8%, STRD 10% of the $100 stated amount; STRC\u2019s board-set monthly rate), only the frequency does. DiviDrip annualizes these as single-day accrual \u00d7 365 so the yield you see matches the stated rate, and they drop out of the Preferred Shares screener once daily payouts begin because their payout is no longer a fixed per-period coupon.',
  },
  {
    q: 'Does the stock price drop by the dividend amount every day?',
    a: 'Mathematically yes, by exchange rule. But at roughly $0.05 per day the price adjustment is completely swallowed by SATA\u2019s normal intraday volatility. Under the old monthly model SATA dropped ~$1.08 every ex-date, creating predictable clustered volatility. Spreading the payout across ~252 days flattens that spike into invisible noise.',
  },
  {
    q: 'Does DRIP work with daily payers?',
    a: 'Only if your broker supports fractional share purchases at four-decimal precision (Fidelity, Charles Schwab, Vanguard, most modern brokerages). At $0.05/day per share you cannot reinvest into whole shares — the payout has to compound into fractionals. If your broker only reinvests whole shares, the daily cash will just accumulate and the compounding advantage is lost. Compounding 252 times a year instead of 12 lifts the effective annual yield from ~13.80% (monthly DRIP) to ~13.88% (daily DRIP).',
  },
  {
    q: 'How are daily dividends taxed?',
    a: 'The frequency does not change the tax treatment — your broker pools all ~250 daily payouts into one line on your year-end 1099. But SATA specifically has a bigger twist: because Strive funds the distributions from capital raises and treasury reserves rather than corporate earnings, they are classified as Return of Capital (ROC), reported in Box 3 of the 1099-DIV. In a taxable account ROC is not taxed when received — it lowers your cost basis instead, deferring the tax to a larger capital gain when you sell (and once basis hits $0, further payouts become immediately taxable gains). This makes a tax-advantaged wrapper (Roth IRA, Traditional IRA, or Solo 401(k)) especially valuable, since none of the basis tracking applies there.',
  },
  {
    q: 'Why is a Roth IRA the ideal wrapper for a daily-dividend stock?',
    a: 'Three reasons: (1) all distributions grow tax-free so you skip the 10-37% ordinary-income tax drag entirely; (2) the ~252 daily cost-basis lots your broker generates each year never need to be tracked for capital-gains purposes because Roth withdrawals of qualified earnings are tax-free; (3) you avoid the "reinvestment tax trap" — in a taxable account you owe income tax on dividends you never touched (because DRIP swept them into more shares), forcing you to find outside cash to cover the tax bill. In a Roth, $0.00 is owed at tax time. The 2026 Roth IRA contribution limit is $7,500 ($8,600 with the $1,100 age-50+ catch-up); MAGI phase-outs start at $153,000 single / $242,000 joint.',
  },
  {
    q: 'Will more stocks start paying daily?',
    a: 'Possibly. Strive built the infrastructure to prove it works, and the tech (DTC-tracked daily record-date reconciliation, T+1 settlement, sub-penny fractional payouts) is now off-the-shelf. Any other issuer that wants to flatten ex-date volatility and appeal to income-focused investors could copy the model. Our platform auto-detects daily cadence from the payout gap history, so any new daily payer will show up correctly on DiviDrip without a code change.',
  },
];

export default function LearnDailyDividendStocks() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Daily Dividend Stocks — SATA, CHAD and Strategy's STRC/STRF/STRK/STRD | DiviDrip"
      description="Strive's SATA became the first US-listed stock to pay a dividend every business day (June 16, 2026); CHAD followed, and Strategy's STRC, STRF, STRK and STRD now accrue 365 days a year after the Oct 28, 2026 vote. How 5-day vs 7-day daily models, ex-dates, DRIP compounding and Roth taxes work."
      canonical="https://twylightcrow.com/learn/daily-dividend-stocks"
      guidePath="/learn/daily-dividend-stocks"
      sectionLabel="Dividend Basics"
      sectionAnchor="basics"
      kicker="Learn · Firsts in dividend history"
      h1="Daily Dividend Stocks &mdash; The First-Ever Daily Payer (SATA)"
      datePublished="2026-08-26"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/monthly-vs-quarterly-dividends', label: 'Monthly vs Quarterly dividends' },
        { to: '/learn/ex-dividend-timeline', label: 'The ex-dividend timeline explained' },
        { to: '/learn/drip-vs-cash', label: 'DRIP vs cash distributions' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP is most powerful inside a Roth IRA' },
        { to: '/learn/qualified-dividends', label: 'Qualified vs ordinary dividends' },
        { to: '/learn/cost-basis-methods', label: 'Cost basis methods (FIFO vs Average)' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        On June 16, 2026, <Link to="/stock/SATA">SATA</Link> &mdash; the 13.00% Series A
        preferred stock issued by <em>Strive, Inc.</em> &mdash; became the first
        publicly-traded US stock to pay a cash dividend every single business day.
        No stock, ETF, REIT, BDC, or preferred issue had ever done this before. The
        shift is a genuine dividend-history first.
      </p>
      <p>
        Before June 2026 the fastest cash-paying cadence in the US market was
        monthly (Realty Income, STAG, Main Street Capital, JEPI, and roughly
        140 other tickers). Weekly and bi-weekly cadences exist in theory but
        no US-listed common or preferred stock actually pays weekly in practice.
        Daily was uncharted territory until Strive built the plumbing to make
        it work.
      </p>

      <h2>Why Strive did it</h2>
      <p>
        Under the old monthly framework, SATA dropped by more than $1.08 all
        at once on every ex-dividend date. That created two problems:
        predictable trading spikes right around the ex-date (arbitrage bots and
        dividend capture strategies clustered on those days), and an artificial
        chart look that made the stock appear more volatile than the underlying
        business actually was.
      </p>
      <p>
        Spreading the same $13.00 annual payout across roughly 252 business
        days means the ex-date adjustment shrinks to about $0.05 per morning
        &mdash; well below the normal intraday range. The monthly volatility
        spike disappears entirely.
      </p>

      <h2>The mechanics of daily ex-dates</h2>
      <p>
        Every business day is simultaneously its own ex-dividend date, its own
        record date, and (one business day later) its own payment date. Because
        the US market settles at T+1, buying SATA on Monday means you settle
        Tuesday, become holder of record Tuesday, and earn Tuesday&apos;s dividend
        (paid Wednesday).
      </p>
      <p>
        Selling works the mirror image: sell on Monday, settle on Tuesday, still
        the holder of record on Monday night &mdash; you collect Monday&apos;s
        dividend even though you kicked off the sale that morning. Your broker
        does all this bookkeeping automatically.
      </p>
      <p>
        The per-day amount varies slightly month-to-month because the number of
        business days in each month is different. Strive keeps the annual rate
        pinned at exactly 13.00% of the $100 stated value ($13.00/share):
      </p>
      <ul>
        <li><strong>June 2026</strong> &mdash; 11 business days (partial month from June 16
          launch, plus Juneteenth holiday) &rarr; <strong>$0.0493 per day</strong>.</li>
        <li><strong>July 2026</strong> &mdash; 22 business days &rarr; <strong>$0.0493 per day</strong>.</li>
        <li><strong>August 2026</strong> &mdash; 21 business days &rarr; <strong>$0.0516 per day</strong>.</li>
        <li><strong>September 2026</strong> &mdash; 21 business days &rarr; <strong>$0.0516 per day</strong>.</li>
      </ul>

      <h2>What daily DRIP unlocks</h2>
      <p>
        Compounding roughly 252 times a year instead of 12 nudges the effective
        annual yield up from the stated 13.00% to about 13.88% (an extra
        ~0.08 percentage points from optimal timing). On a $10,000 position
        that translates to roughly $7.60 of extra income per year purely from
        hyper-frequent reinvestment.
      </p>
      <p>
        Two prerequisites make daily DRIP actually work:
      </p>
      <ol>
        <li><strong>Fractional shares to four decimal places.</strong> A
          $0.05 payout on a $100 stock buys 0.0005 shares. Brokers that only
          reinvest into whole shares will let the cash sit idle, killing the
          compounding advantage. Fidelity, Schwab, Vanguard, and most modern
          discount brokerages handle sub-penny fractionals cleanly.</li>
        <li><strong>Zero DRIP commissions.</strong> Any per-transaction fee
          (even a nickel) applied 252 times a year quickly eats the incremental
          yield. Confirm your broker&apos;s DRIP is free before turning it on
          for a daily payer.</li>
      </ol>

      <h2>The tax angle &mdash; why this belongs in a Roth</h2>
      <p>
        SATA distributions are non-qualified ordinary dividends because they
        come from a preferred stock issued by a corporate-treasury operating
        company, not a common equity in an SEC-defined holding period. That
        means the dividends are taxed at your ordinary federal rate (10&ndash;37%
        in 2026), not the friendlier 0/15/20% qualified rate.
      </p>
      <p>
        Inside a <Link to="/learn/why-drip-roth">Roth IRA</Link> that tax hit
        vanishes entirely. Three specific benefits:
      </p>
      <ul>
        <li><strong>No ordinary-income drag.</strong> A 24% federal bracket
          on 13.00% of yield eats roughly 3.12 percentage points per year in
          a taxable account. In a Roth you keep the full 13.00%.</li>
        <li><strong>No reinvestment tax trap.</strong> Daily DRIP in a taxable
          account triggers ordinary income tax on cash you never see (the
          broker swept it straight into more shares). You still owe the tax
          in April and have to find outside cash to pay it. Inside a Roth,
          $0.00 is owed at tax time regardless of how many shares you accumulated.</li>
        <li><strong>No cost-basis tracking headache.</strong> A daily DRIP
          generates roughly 252 individual cost-basis lots per year. If you
          ever sell in a taxable account, your broker has to reconcile every
          fractional lot. In a Roth, no basis tracking is needed for
          qualified withdrawals (age 59&frac12; and 5-year holding rule).</li>
      </ul>
      <p>
        The 2026 Roth IRA contribution ceiling is <strong>$7,500</strong> under age 50
        and <strong>$8,600</strong> once you turn 50 (a $1,100 catch-up on top of the
        base limit). Contributions phase out at MAGI of $153,000&ndash;$168,000 for single
        filers and $242,000&ndash;$252,000 for married-filing-jointly. If you want to
        deploy more than the annual limit into SATA immediately, you have to reallocate
        existing Roth balances or execute a Traditional-to-Roth conversion &mdash; you
        cannot simply deposit more new cash.
      </p>

      <h2>How DiviDrip handles daily payers</h2>
      <p>
        Our data pipeline detects daily cadence automatically. When the median
        gap between the last ten ex-dates is three calendar days or less, we
        flip the ticker to <em>Daily</em> frequency and project the next ex-date
        onto the next business day (skipping weekends and NYSE holidays). The
        annual rate depends on which of the two daily models the issuer uses
        (next section): median daily amount &times; 252 for business-day payers like
        SATA and CHAD, or single-day accrual &times; 365 for calendar-day payers like
        Strategy&apos;s preferreds. Either way it is automatic &mdash; no code change per ticker.
      </p>
      <p>
        On the Dividend Info tab of any daily payer&apos;s Stock Modal you&apos;ll
        see:
      </p>
      <ul>
        <li>Payment frequency labeled <strong>Daily</strong>.</li>
        <li>Next ex-date shown as <strong>next business day</strong> instead of a
          countdown &mdash; because a &ldquo;34 days away&rdquo; pill stops being useful
          when every business day is an ex-date.</li>
        <li>A cyan <em>cadence-switched</em> banner on the Streak Risk chart
          for stocks (like SATA) that changed cadence mid-year, explaining
          why the current year&apos;s bar dwarfs the prior year without
          triggering a false &ldquo;possible liquidation&rdquo; warning.</li>
      </ul>

      <h2 id="who-pays-daily">Who pays daily now &mdash; and the 5-day vs 7-day split</h2>
      <p>
        SATA was first (June 16, 2026). DeFi Development Corp.&apos;s CHAD became the second
        daily payer with its first regular daily dividend on October 1, 2026, and Strategy&apos;s
        four preferreds follow from November. As of late 2026 there are two families of daily
        payers, and they do <strong>not</strong> accrue the same way:
      </p>
      <div className="not-prose my-4 overflow-x-auto rounded-xl border border-border" data-testid="learn-daily-payers-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-semibold">Ticker</th>
              <th className="px-3 py-2 font-semibold">Issuer</th>
              <th className="px-3 py-2 font-semibold">Accrues</th>
              <th className="px-3 py-2 font-semibold">Daily since</th>
              <th className="px-3 py-2 font-semibold">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border font-mono text-xs">
            <tr data-testid="learn-daily-row-SATA"><td className="px-3 py-2 font-bold">SATA</td><td className="px-3 py-2 font-sans">Strive (Bitcoin treasury)</td><td className="px-3 py-2">5 days / week &middot; ~252 payments</td><td className="px-3 py-2">Jun 2026 (first ever)</td><td className="px-3 py-2 font-sans">Variable, board-set monthly</td></tr>
            <tr data-testid="learn-daily-row-CHAD"><td className="px-3 py-2 font-bold">CHAD</td><td className="px-3 py-2 font-sans">DeFi Development Corp. (Solana treasury)</td><td className="px-3 py-2">5 days / week &middot; ~252 payments</td><td className="px-3 py-2">Oct 1, 2026</td><td className="px-3 py-2 font-sans">Variable, board-set monthly</td></tr>
            <tr data-testid="learn-daily-row-STRC"><td className="px-3 py-2 font-bold">STRC</td><td className="px-3 py-2 font-sans">Strategy Inc. (Stretch)</td><td className="px-3 py-2">7 days / week &middot; 365 accrual days</td><td className="px-3 py-2">Nov 1, 2026 (first payout Nov 2)</td><td className="px-3 py-2 font-sans">Variable, board-set monthly</td></tr>
            <tr data-testid="learn-daily-row-STRF"><td className="px-3 py-2 font-bold">STRF</td><td className="px-3 py-2 font-sans">Strategy Inc. (Strife)</td><td className="px-3 py-2">7 days / week &middot; 365 accrual days</td><td className="px-3 py-2">Jan 1, 2027 (first payout Jan 4)</td><td className="px-3 py-2 font-sans">10.00% of $100</td></tr>
            <tr data-testid="learn-daily-row-STRK"><td className="px-3 py-2 font-bold">STRK</td><td className="px-3 py-2 font-sans">Strategy Inc. (Strike)</td><td className="px-3 py-2">7 days / week &middot; 365 accrual days</td><td className="px-3 py-2">Jan 1, 2027 (first payout Jan 4)</td><td className="px-3 py-2 font-sans">8.00% of $100</td></tr>
            <tr data-testid="learn-daily-row-STRD"><td className="px-3 py-2 font-bold">STRD</td><td className="px-3 py-2 font-sans">Strategy Inc. (Stride)</td><td className="px-3 py-2">7 days / week &middot; 365 accrual days</td><td className="px-3 py-2">Jan 1, 2027 (first payout Jan 4)</td><td className="px-3 py-2 font-sans">10.00% of $100</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>SATA and CHAD (5-day model).</strong> Every NYSE trading day is its own
        ex-date, record date and pay date; Saturdays, Sundays and market holidays simply
        don&apos;t exist for the dividend. Each month&apos;s rate is divided evenly across that
        month&apos;s trading days, so every payment in a month is identical and the year holds
        roughly 252 of them.
      </p>
      <p>
        <strong>STRC, STRF, STRK and STRD (7-day model).</strong> On October 28, 2026 Strategy
        Inc. common shareholders approved the Daily Dividends amendment with 99.3% of votes in
        favor. The dividend now <em>accrues every calendar day</em>, weekends and holidays
        included &mdash; 365 accrual days a year. Because the shares clear through DTC, which
        cannot settle on a weekend, Saturday and Sunday&apos;s accrual is bundled onto Friday&apos;s
        record date and paid out the following business day. So a Friday payment is roughly
        three times a Tuesday payment, a payment before a three-day weekend is four times, and
        the <em>number</em> of actual payouts is still about 252 &mdash; but they add up to a full
        365 days of accrual. The annual rates did not change: STRF 10%, STRK 8% and STRD 10% of
        the $100 stated amount, and STRC&apos;s board-set monthly rate. Only the frequency did.
        Strategy&apos;s stated goal is to kill the predictable price slippage around large
        monthly and quarterly ex-dates and keep the preferreds pinned tightly to par.
      </p>
      <p>
        <strong>Why the split matters for the yield you see.</strong> Take a 10% coupon on $100.
        The 5-day model pays $10.00 &divide; 252 = $0.0397 every trading day; multiplying the
        typical payment by 252 gets you back to $10.00. The 7-day model pays $10.00 &divide; 365
        = $0.0274 per calendar day, but Monday-through-Thursday payments are one day each while
        Friday carries three. Multiply a typical $0.0274 payment by 252 and you&apos;d show $6.90
        &mdash; a 31% understatement of a dividend that is really $10.00. That is why DiviDrip
        annualizes SATA/CHAD as median payment &times; 252 and the Strategy four as single-day
        accrual &times; 365, and why a quick &ldquo;last payment &times; payments per year&rdquo;
        on a brokerage screen can be badly wrong for STRC-style payers.
      </p>
      <p>
        One housekeeping note: once STRC/STRF/STRK/STRD begin paying daily they leave the{' '}
        <Link to="/screener?is_preferred=only" data-testid="learn-daily-pref-screener-link">Preferred Shares screener</Link> view. Our
        yield-to-call and call-protection math assumes a fixed per-period coupon, and a payout
        that accrues by the calendar day doesn&apos;t fit that model &mdash; they keep their
        Treasury Vehicle badge and par anchoring analysis instead.
      </p>

      <h2>What SATA actually is &mdash; a treasury vehicle, not a business</h2>
      <p>
        One thing worth understanding before you size a position: SATA is not an operating
        company&apos;s common stock. It is a <strong>variable-rate perpetual preferred issued to
        fund Strive&apos;s Bitcoin treasury</strong>. The dividend is paid on a $100 stated par
        value, funded by capital raises and treasury reserves rather than earnings, and the board
        re-sets the rate every month. That is why classic metrics like EPS growth, ROE and payout
        ratio look broken for it &mdash; they simply don&apos;t apply. DiviDrip flags these tickers
        with a <strong>Treasury Vehicle</strong> badge and grades them on par anchoring, the
        declaration record, and sponsor leverage instead. The full breakdown lives in our{' '}
        <Link to="/learn/treasury-vehicle-preferreds">Treasury-Vehicle Preferreds guide</Link>.
      </p>

      <h2>Should you buy SATA?</h2>
      <p>
        That&apos;s outside the scope of this guide &mdash; run it through the
        DiviDrip <Link to="/triangle/SATA">Dividend Triangle</Link> checklist
        first. Two things to watch specifically:
      </p>
      <ol>
        <li><strong>It&apos;s a preferred, not a common.</strong> That means
          the 13.00% rate is contractual (Strive committed to it in the
          issuance documents), but the upside is capped &mdash; you won&apos;t
          get the underlying business&apos; growth. If Strive&apos;s
          Bitcoin-treasury strategy 10-bags, common-share holders capture that
          gain, not SATA holders.</li>
        <li><strong>The dividend rate depends on Strive&apos;s cash flow.</strong>{' '}
          If Bitcoin drops enough that Strive can&apos;t cover the accrued daily
          coupons, the preferred can defer or reduce distributions. Read the
          latest 10-Q before sizing the position.</li>
      </ol>

      <p className="italic text-sm text-muted-foreground mt-8">
        SATA is a first-of-its-kind product and its structure may evolve. This
        guide reflects the daily-payout mechanics as they existed at launch
        (June 16, 2026) and were operating as of August 2026. If Strive
        modifies the cadence, we&apos;ll update this page.
      </p>
    </LearnPage>
  );
}
